/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import { permissionObj } from '../../../Helpers/permission';
import { status } from '../../../enums/statusEnum';
import { statusBorderColor, statusColor, statusFontColor } from '../../../enums/statusColorEnum';
import HeadTittle from '../../../components/HeadTittle';
import routes from '../../../Routes/routes';
import CustomCard from '../../../components/CustomCard';
import CustomChip from '../../../components/CustomChip';
import {DATE_TIME_FORMAT_NAME_MODEL, EMPTY_VALUES, getDateFormat, userDataTransform} from '../../../Apis/functions';
import {
    AddIcon,
    CarTitle,
    CompletePersonChip,
    DocumentMedalIcon,
    DocumentLinesIcon,
    EmailSubsectionChip,
    IconAuditTrail,
    IconBuildingTable,
    IconScheduleTable,
    TrashOutlineIcon,
    MobileIcon,
} from '../../../components/buttons/IconButtons';
import ButtonAction from '../../../components/buttons/ButtonAction';
import ContractorDetailsApp from './Functionality/ContractorDetailsApp';
import ContractVehicleDetailsCards from './SubComponents/ContractVehicleDetailsCards';
import ContractorEmployeeDetailsCards from './SubComponents/ContractorEmployeeDetailsCards';
import CustomTooltip from '../../../components/CustomTooltip';
import CustomTabContainer from '../../../components/CustomTabContainer';
import { CONTRACT_DETAILS_TABS, CONTRACT_DETAILS_VIEW } from './Model/Models';
import CustomTab from '../../../components/CustomTab';
import AddEmployeesModal from './modal/AddEmployeesModal';
import AddVehiclesModal from './modal/AddVehiclesModal';
import ConfirmModal from '../Visitor/Invitation/Modal/ConfirmModal';
import CustomGenericTittleIconDataChip from "../../../components/CustomGenericTittleIconDataChip";
import DivContainerBody from "../../../components/DivContainerBody";

const ContractDetails = () => {
    const {
        t,
        navigate,
        modified,
        setModified,
        DEV,
        permission,
        contractId,
        deleteShowContract,
        setDeleteShowContract,
        contractDetail,
        setAddUserModal,
        selectEmployeeForDelete,
        deleteShowEmployee,
        setDeleteShowEmployee,
        deleteShowVehicle,
        setDeleteShowVehicle,
        setSelectVehicleForDelete,
        addUserModal,
        addVehicleModal,
        deleteEmployee,
        deleteVehicle,
        deleteContract,
        setAddVehicleModal,
        selectVehicleForDelete,
        setSelectEmployeeForDelete,
        isAllCheckedEmployee,
        setIsAllCheckedEmployee,
        isAllCheckedVehicle,
        setIsAllCheckedVehicle,
        subView,
        handleSubViewChange,
        contractorId,
        availableHeightColumnOneRef,
        availableHeightColumnTwoRef,
        containerHeightRef,
        containerHeightCardsRef
    } = ContractorDetailsApp();

    if (!DEV && !permission?.includes(permissionObj.WEB_CONTRACTOR_CONTRACT_READ) && contractorId === null)
        navigate(routes.forbidden);
    const isEmployee = subView === CONTRACT_DETAILS_VIEW.EMPLOYEE;

    return (
        <>
            <HeadTittle name={'details'} link={contractorId ? routes.contractor.contractList: routes.contractor.list}/>
            <DivContainerBody>
                <div className='row px-0 h-100'>
                    <div className='col-sm-12 col-md-4 col-lg-4 mb-lg-0 mb-md-3 mb-sm-3 pl-0' ref={availableHeightColumnOneRef}>
                        <CustomCard classes={'h-100'} classCard={'card-border-8px'} classContent={'p-0'}>
                            <div className='d-flex justify-content-between align-items-center m-3'>
                                <CustomGenericTittleIconDataChip
                                    title={'contract_details'}
                                    Icon={DocumentLinesIcon}
                                    border={false}
                                    titleBoxClass={'d-flex'}
                                />
                                <CustomChip
                                    variant={'large'}
                                    background={statusColor[contractDetail?.statusId]}
                                    fontColor={statusFontColor[contractDetail?.statusId]}
                                    borderColor={statusBorderColor[contractDetail?.statusId]}
                                    legend={t(status[contractDetail?.statusId]) || EMPTY_VALUES.DEFAULT}
                                />
                            </div>
                            <CustomCard classCard={'card-border-8px m-3'} classContent={'p-0'}>
                                <div className={'d-flex align-items-center m-3'}>
                                    <IconAuditTrail className='h-24px w-24px' />
                                    <p className='fs-14px font-color-general-950 ibl-font-w-600 ml-2'>{t('trail')}</p>
                                </div>
                                <div className={'border-bottom-shades-300'} />
                                <div className='row align-items-start p-3 overflow-y-auto' ref={containerHeightRef} >
                                    <div className='col-2 p-0'>
                                        <div className='p-8px border-radius-8px d-flex w-44px h-44px justify-content-center background-secondary-100 mt-4'>
                                            <DocumentMedalIcon className='btn-icon-svg-24px' />
                                        </div>
                                    </div>
                                    <div className='col-10'>
                                        <div className='row my-4 px-0 ml-0 pl-0'>
                                            <div className='pl-0 ml-0'>
                                                <CustomTooltip
                                                    text={contractDetail?.folio?.toUpperCase() || EMPTY_VALUES.DEFAULT}
                                                    classes={`ffs-14px font-color-general-950 text-ellipsis-1-row ibl-font-w-600 pl-0 ml-0`}
                                                    showIfTruncated={true}
                                                />
                                            </div>
                                            <div className='col-12 px-0'>
                                                <div className='d-flex align-items-center'>
                                                    <IconBuildingTable />
                                                    <CustomTooltip
                                                        text={contractDetail?.contractorCompanyName}
                                                        classes={'font-color-general-950 fs-14px ml-2'}
                                                        showIfTruncated={true}
                                                        placement={'top'}
                                                    />
                                                </div>
                                            </div>
                                            <div className='border-bottom-shades-300 mt-3' />
                                            <div className='col-12 px-0'>
                                                <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0 my-3'>
                                                    {t('contractor_information')}
                                                </p>
                                                <p className='font-color-general-950 fs-14px ibl-font-w-400 px-0 mt-3 mb-1'>
                                                    {contractDetail?.fullName}
                                                </p>
                                            </div>
                                            <div className={'row mt-2 ml-0 pl-0 ml-0'}>
                                                <div className={'d-flex justify-content-between p-0 gap-3 '}>
                                                    <div className={'d-flex flex-grow-1 minw-0 align-items-center'}>
                                                        <div className={'mr-2 d-flex align-items-center'}>
                                                            <EmailSubsectionChip />
                                                        </div>
                                                        <div className={'flex-grow-1 minw-0'}>
                                                            <CustomTooltip
                                                                text={contractDetail?.contractorEmail}
                                                                classes={`fs-14px font-color-general-950 text-ellipsis-1-row align-items-center`}
                                                                showIfTruncated={true}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={'d-flex flex-grow-1 minw-0'}>
                                                        <div className={'mr-2 d-flex align-items-center'}>
                                                            <MobileIcon className='icon-color-shades-800 flex-shrink-0' />
                                                        </div>
                                                        <div className={'flex-grow-1 minw-0'}>
                                                            <CustomTooltip
                                                                text={contractDetail?.contractorPhoneNumber}
                                                                classes={`fs-14px font-color-general-950 text-ellipsis-1-row`}
                                                                showIfTruncated={true}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='border-bottom-shades-300 mt-3' />
                                            <div className='row px-0 gap-2 mt-3'>
                                                <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0'>
                                                    {t('start_date')}
                                                </p>
                                                <div className='d-flex align-items-center px-0'>
                                                    <IconScheduleTable />
                                                    <p className='font-color-general-950 fs-14px ml-2'>
                                                        {getDateFormat(
                                                            contractDetail?.startDate,
                                                            false,
                                                            DATE_TIME_FORMAT_NAME_MODEL.shortMonth,
                                                        )}
                                                    </p>
                                                </div>
                                                <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0'>
                                                    {t('end_date')}
                                                </p>
                                                <div className='d-flex align-items-center px-0'>
                                                    <IconScheduleTable />
                                                    <p className='font-color-general-950 fs-14px ml-2'>
                                                        {getDateFormat(
                                                            contractDetail?.endDate,
                                                            false,
                                                            DATE_TIME_FORMAT_NAME_MODEL.shortMonth,
                                                        )}
                                                    </p>
                                                </div>
                                                <div className='border-bottom-shades-300 mt-3'/>
                                                <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0'>
                                                    {t('description')}
                                                </p>
                                                <p className='font-color-general-950 fs-14px px-0'>
                                                    {contractDetail?.description || EMPTY_VALUES.DEFAULT}
                                                </p>
                                                <div className='border-bottom-shades-300 mt-3'/>
                                                <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0 my-3'>
                                                    {t('resource_summary')}
                                                </p>
                                                <CustomCard
                                                    classCard=''
                                                    classContent='px-0'
                                                    classes='px-0 border-full-success-150 card-border-8px background-success-50'
                                                >
                                                    <div className='d-flex align-items-center justify-content-between ml-3 mt-1'>
                                                        <div className='d-flex align-items-center gap-2'>
                                                            <CompletePersonChip
                                                                width={32}
                                                                height={32}
                                                                className='icon-color-success-600'
                                                            />
                                                            <p className='ibl-font-w-700 font-color-success-600 m-0'>
                                                                {t('employees')}
                                                            </p>
                                                        </div>
                                                        &nbsp;
                                                        <p className='ibl-font-w-700 fs-14px font-color-success-600 m-0 mr-3'>
                                                            {contractDetail?.totalEmployees ?? EMPTY_VALUES.DEFAULT}
                                                        </p>
                                                    </div>
                                                </CustomCard>
                                                <div className='mt-3 ml-0 mr-0 px-0'>
                                                    <CustomCard
                                                        classCard=''
                                                        classContent='px-0'
                                                        classes='px-0 border-full-secondary-150 card-border-8px background-secondary-50'
                                                    >
                                                        <div className='d-flex align-items-center justify-content-between ml-3 mt-1'>
                                                            <div className='d-flex align-items-center gap-2'>
                                                                <CarTitle
                                                                    width={32}
                                                                    height={32}
                                                                    className='icon-color-secondary-600'
                                                                />
                                                                <p className='ibl-font-w-700  font-color-secondary-600 m-0'>
                                                                    {t('vehicles')}
                                                                </p>
                                                            </div>

                                                            <p className='ibl-font-w-700 fs-14px font-color-secondary-600 m-0 mr-3'>
                                                                {contractDetail?.totalVehicles ?? EMPTY_VALUES.DEFAULT}
                                                            </p>
                                                        </div>
                                                    </CustomCard>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CustomCard>
                        </CustomCard>
                    </div>
                    <div className='col-sm-12 col-md-8 col-lg-8 pl-0' ref={availableHeightColumnTwoRef}>
                        <CustomCard classes={'h-100'} classCard={'card-border-8px '} classContent={'p-0'}>
                            <div className='d-flex align-items-center justify-content-between'>
                                <div className='w-300px'>
                                    <CustomTabContainer
                                        activeTab={subView}
                                        setActiveTab={handleSubViewChange}
                                        className='rounded-top border-0 px-3'
                                    >
                                        {CONTRACT_DETAILS_TABS.map((item) => (
                                            <CustomTab
                                                key={item.key}
                                                id={item.key}
                                                title={t(item.translation)}
                                                isSelected={subView === item.key}
                                                label={t(item.translation)}
                                                icon={item.icon}
                                                selected={subView === item.key}
                                                onClick={() => handleSubViewChange(item.key)}
                                            />
                                        ))}
                                    </CustomTabContainer>
                                </div>
                                <div className='d-flex gap-2 m-3'>
                                    {contractorId && (
                                        <ButtonAction
                                            id={'btn_cd_add_element'}
                                            name='add'
                                            className='btn-secondary-600'
                                            Icon={AddIcon}
                                            splitName={false}
                                            onClick={() => {
                                                if (subView === CONTRACT_DETAILS_VIEW.EMPLOYEE) {
                                                    setAddUserModal(true);
                                                } else {
                                                    setAddVehicleModal(true);
                                                }
                                            }}
                                            disabled={false}
                                        />
                                    )}
                                    {/* Contractors manage their own contract, employees need the manage permission of the tab */}
                                {(contractorId || DEV || permission?.includes(isEmployee
                                    ? permissionObj?.WEB_CONTRACTOR_CONTRACT_MANAGE_USERS
                                    : permissionObj?.WEB_CONTRACTOR_CONTRACT_MANAGE_VEHICLES)) && (
                                    <ButtonAction
                                        id='btn_cd_remove'
                                        name='remove'
                                        className='btn-icon-delete justify-content-end'
                                        disabled={
                                            isEmployee
                                                ? selectEmployeeForDelete.length === 0
                                                : selectVehicleForDelete.length === 0
                                        }
                                        Icon={TrashOutlineIcon}
                                        onClick={() =>
                                            isEmployee ? setDeleteShowEmployee(true) : setDeleteShowVehicle(true)
                                        }
                                        />
                                    )}
                                </div>
                            </div>
                            <div className={'border-bottom-shades-300 mt-1 mb-3'}/>
                            {subView === CONTRACT_DETAILS_VIEW.EMPLOYEE && (
                                <ContractorEmployeeDetailsCards
                                    contractorId={contractorId}
                                    selectedList={selectEmployeeForDelete}
                                    setSelectedList={setSelectEmployeeForDelete}
                                    setDeleteShow={setDeleteShowEmployee}
                                    modified={modified}
                                    setModified={setModified}
                                    userDataTransform={userDataTransform}
                                    isAllChecked={isAllCheckedEmployee}
                                    setIsAllChecked={setIsAllCheckedEmployee}
                                    subView={subView}
                                    containerHeightCardsRef={containerHeightCardsRef}
                                />
                            )}
                            {subView === CONTRACT_DETAILS_VIEW.VEHICLE && (
                                <ContractVehicleDetailsCards
                                    contractorId={contractorId}
                                    selectedList={selectVehicleForDelete}
                                    setSelectedList={setSelectVehicleForDelete}
                                    setDeleteShow={setDeleteShowVehicle}
                                    modified={modified}
                                    setModified={setModified}
                                    isAllChecked={isAllCheckedVehicle}
                                    setIsAllChecked={setIsAllCheckedVehicle}
                                    subView={subView}
                                    containerHeightCardsRef={containerHeightCardsRef}
                                />
                            )}
                        </CustomCard>
                    </div>
                </div>
            </DivContainerBody>
            <ConfirmModal
                show={deleteShowContract}
                onHide={() => setDeleteShowContract(false)}
                onClickFn={() => deleteContract([contractId])}
                title_modal='delete'
                description={t('are_you_sure_you_want_to_delete')}
            />

            <ConfirmModal
                show={deleteShowEmployee}
                onHide={() => setDeleteShowEmployee(false)}
                onClickFn={() => deleteEmployee(selectEmployeeForDelete)}
                isReset={setSelectEmployeeForDelete}
                isAllReset={setIsAllCheckedEmployee}
                title_modal='remove_employee'
                description={t('are_you_sure_you_want_to_remove')}
            />

            <ConfirmModal
                show={deleteShowVehicle}
                onHide={() => setDeleteShowVehicle(false)}
                onClickFn={() => deleteVehicle(selectVehicleForDelete)}
                isReset={setSelectVehicleForDelete}
                isAllReset={setIsAllCheckedVehicle}
                title_modal='remove_vehicle'
                description={t('are_you_sure_you_want_to_remove')}
            />
            {addUserModal && (
                <AddEmployeesModal
                    show={addUserModal}
                    onHide={() => setAddUserModal(false)}
                    selectedId={contractId}
                    setModified={setModified}
                    modified={modified}
                />
            )}
            {addVehicleModal && (
                <AddVehiclesModal
                    show={addVehicleModal}
                    onHide={() => setAddVehicleModal(false)}
                    selectedId={contractId}
                    setModified={setModified}
                    modified={modified}
                />
            )}
        </>
    );
};

export default ContractDetails;
